<!-- Image Editor Overlay -->
@if (visible) {
  <div class="image-editor-overlay">
    <div class="image-editor-container">
      <!-- Header -->
      <div class="editor-header">
        <div class="header-left">
          <span class="editor-title">{{ productName || ('imageEditor.imageEditor' | transloco) }}</span>
        </div>
        <div class="header-right">
          <button class="btn-reset-header" (click)="restoreOriginal()" [title]="'imageEditor.resetToOriginal' | transloco">
            {{ 'imageEditor.reset' | transloco }}
          </button>
          <button class="btn-save-header" (click)="saveImage()" [title]="'imageEditor.saveImage' | transloco">
            {{ 'imageEditor.saveImage' | transloco }}
          </button>
          <button class="close-icon-btn" (click)="cancel()" [title]="'imageEditor.close' | transloco">
            ✕
          </button>
        </div>
      </div>
      <!-- Menu Bar (Full Width) - Hidden during aspect ratio correction -->
      @if (!showAspectRatioCorrectionPanel) {
        <div class="menu-bar">
          <button class="menu-item" (click)="triggerFileInput()" [title]="'imageEditor.addNewImage' | transloco">
            <span class="menu-icon">+</span>
            <span class="menu-label">{{ 'imageEditor.image' | transloco }}</span>
          </button>
          <div class="menu-divider"></div>
          <button class="menu-item" [class.active]="activeAccordion === 'enhance'" (click)="toggleAccordion('enhance')" [title]="'imageEditor.enhanceSubject' | transloco">
            <span class="menu-icon">🎨</span>
            <span class="menu-label">{{ 'imageEditor.enhance' | transloco }}</span>
          </button>
          <button class="menu-item" (click)="removeBackground()" [disabled]="isProcessing || backgroundRemoved" [title]="'imageEditor.removeBackground' | transloco">
            <span class="menu-icon">✂️</span>
            <span class="menu-label">{{ 'imageEditor.removeBg' | transloco }}</span>
          </button>
          <button class="menu-item" [class.active]="activeAccordion === 'background'" (click)="toggleAccordion('background')" [title]="'imageEditor.backgroundSettings' | transloco">
            <span class="menu-icon">🖼️</span>
            <span class="menu-label">{{ 'imageEditor.background' | transloco }}</span>
          </button>
          <button class="menu-item" [class.active]="activeAccordion === 'shadow'" (click)="toggleAccordion('shadow')" [title]="'imageEditor.shadowSettings' | transloco">
            <span class="menu-icon">◐</span>
            <span class="menu-label">{{ 'imageEditor.shadow' | transloco }}</span>
          </button>
          <div class="menu-divider"></div>
          <button class="menu-item" [class.active]="perspectiveCorrectionMode" (click)="startPerspectiveCorrection()" [title]="'imageEditor.perspectiveCorrection' | transloco">
            <span class="menu-icon">📐</span>
            <span class="menu-label">{{ 'imageEditor.perspective' | transloco }}</span>
          </button>
          <button class="menu-item" [class.active]="perspectiveWarpMode" (click)="togglePerspectiveWarpMode()" [title]="'imageEditor.warpPerspective' | transloco">
            <span class="menu-icon">🔶</span>
            <span class="menu-label">{{ 'imageEditor.warp' | transloco }}</span>
          </button>
          <div class="menu-divider"></div>
          <button class="menu-item" [class.active]="activeAccordion === 'export'" (click)="toggleAccordion('export')" [title]="'imageEditor.exportOptions' | transloco">
            <span class="menu-icon">💾</span>
            <span class="menu-label">{{ 'imageEditor.export' | transloco }}</span>
          </button>
        </div>
      }
      <!-- Main Workspace -->
      <div class="workspace-grid">
        <!-- Left Panel: Image Display -->
        <div class="image-panel">
          <div class="canvas-container">
            <!-- Processing Overlay -->
            @if (isProcessing) {
              <div class="processing-overlay">
                <div class="processing-content">
                  <div class="spinner"></div>
                  <p>{{ processingMessage }}</p>
                </div>
              </div>
            }
            <!-- Dimension Info Label -->
            @if ((correctedImage || originalImage) && !perspectiveCorrectionMode) {
              <div class="dimension-label">
                {{ 'imageEditor.dimensionPixels' | transloco: { width: (correctedImage || originalImage)?.width, height: (correctedImage || originalImage)?.height } }}
              </div>
            }
            <!-- Source canvas for perspective selection -->
            <canvas
              #sourceCanvas
              [style.display]="perspectiveCorrectionMode ? 'block' : 'none'"
              (click)="onCanvasClick($event)"
              (mousemove)="onCanvasMouseMove($event)"
              (mouseup)="onCanvasMouseUp()"
              [class.clickable]="corners.length < 4"
            ></canvas>
            <!-- Preview canvas -->
            <canvas
              #previewCanvas
              [style.display]="!perspectiveCorrectionMode ? 'block' : 'none'"
              [style.cursor]="currentCursor"
              (mousedown)="onPreviewMouseDown($event)"
              (mousemove)="onPreviewMouseMove($event)"
              (mouseup)="onPreviewMouseUp()"
              (mouseleave)="onPreviewMouseLeave()"
            ></canvas>
          </div>
        </div>
        <!-- Right Panel: Layers + Controls -->
        <div class="controls-panel">
          <!-- ==================== ASPECT RATIO CORRECTION PANEL (after perspective correction) ==================== -->
          @if (showAspectRatioCorrectionPanel) {
            <div class="aspect-ratio-correction-panel">
              <div class="panel-header-large">
                <span class="panel-icon">📐</span>
                <span class="panel-title">{{ 'imageEditor.perspectiveCorrected' | transloco }}</span>
              </div>
              <div class="panel-section">
                <h4 class="section-title">{{ 'imageEditor.aspectRatioCorrection' | transloco }}</h4>
                <p class="section-description">{{ 'imageEditor.correctDistorted' | transloco }}</p>
                <!-- Orientation Toggle -->
                <div class="orientation-toggle-large">
                  <button class="orientation-btn-large" [class.active]="aspectOrientation === 'landscape'" (click)="setAspectOrientation('landscape')">
                    ↔ {{ 'imageEditor.landscape' | transloco }}
                  </button>
                  <button class="orientation-btn-large" [class.active]="aspectOrientation === 'portrait'" (click)="setAspectOrientation('portrait')">
                    ↕ {{ 'imageEditor.portrait' | transloco }}
                  </button>
                </div>
                <!-- Ratio Preset Buttons - Large -->
                <div class="ratio-presets-large">
                  <button class="ratio-btn-large" [class.active]="selectedAspectRatio === '1:1'" (click)="applyAspectRatioPreset('1:1')">1:1</button>
                  <button class="ratio-btn-large" [class.active]="selectedAspectRatio === '4:3'" (click)="applyAspectRatioPreset('4:3')">4:3</button>
                  <button class="ratio-btn-large" [class.active]="selectedAspectRatio === '3:2'" (click)="applyAspectRatioPreset('3:2')">3:2</button>
                  <button class="ratio-btn-large" [class.active]="selectedAspectRatio === '16:9'" (click)="applyAspectRatioPreset('16:9')">16:9</button>
                  <button class="ratio-btn-large" [class.active]="selectedAspectRatio === '5:4'" (click)="applyAspectRatioPreset('5:4')">5:4</button>
                </div>
                <button class="btn-reset-ratio" (click)="resetAspectRatio()">
                  {{ 'imageEditor.resetToOriginal' | transloco }}
                </button>
              </div>
              <div class="panel-actions-large">
                <button class="btn-continue" (click)="dismissAspectRatioPanel()">
                  {{ 'imageEditor.continueToEditor' | transloco }} →
                </button>
              </div>
            </div>
          }
          <!-- Layers Panel (Vertical) - Hidden during aspect ratio correction -->
          @if (!showAspectRatioCorrectionPanel) {
            <div class="layers-panel">
              <div class="layers-header">
                <span>{{ 'imageEditor.layers' | transloco }}</span>
              </div>
              <div class="layers-list">
                <!-- Subject Layer -->
                <div class="layer-item" [class.active]="activeLayer === 'subject'" [class.hidden-layer]="!subjectLayerVisible" (click)="selectSubjectLayer()">
                  <span class="layer-visibility" [style.opacity]="subjectLayerVisible ? 1 : 0.3" (click)="toggleLayerVisibility('subject', $event)" [title]="'imageEditor.toggleVisibility' | transloco">👁</span>
                  <span class="layer-name">{{ 'imageEditor.subject' | transloco }}</span>
                  @if (activeLayer === 'subject') {
                    <span class="layer-indicator">●</span>
                  }
                  @if (!subjectLayerVisible) {
                    <span class="layer-status">{{ 'imageEditor.hidden' | transloco }}</span>
                  }
                </div>
                <!-- Shadow Layer -->
                <div class="layer-item" [class.active]="activeLayer === 'shadow'" [class.disabled]="!shadowLayerEnabled" (click)="selectShadowLayer()">
                  <span class="layer-visibility" [style.opacity]="shadowLayerEnabled ? 1 : 0.3" (click)="toggleLayerVisibility('shadow', $event)" [title]="'imageEditor.toggleVisibility' | transloco">👁</span>
                  <span class="layer-name">{{ 'imageEditor.shadow' | transloco }}</span>
                  @if (activeLayer === 'shadow') {
                    <span class="layer-indicator">●</span>
                  }
                  @if (!shadowLayerEnabled) {
                    <span class="layer-status">{{ 'imageEditor.off' | transloco }}</span>
                  }
                </div>
                <!-- Background Layer -->
                <div class="layer-item" [class.active]="activeLayer === 'background'" (click)="selectBackgroundLayer()">
                  <span class="layer-visibility">👁</span>
                  <span class="layer-name">{{ 'imageEditor.background' | transloco }}</span>
                  @if (activeLayer === 'background') {
                    <span class="layer-indicator">●</span>
                  }
                </div>
              </div>
            </div>
          }
          <!-- Accordion Content Panel - Hidden during aspect ratio correction -->
          @if (!showAspectRatioCorrectionPanel) {
            <div class="accordion-content">
              <!-- ==================== ENHANCE (Subject) ACCORDION ==================== -->
              @if (activeAccordion === 'enhance') {
                <div class="accordion-panel">
                  <div class="accordion-header">
                    <span>{{ 'imageEditor.subjectTransformEnhancements' | transloco }}</span>
                  </div>
                  <div class="accordion-body">
                    <!-- ===== Transform Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('transform')">
                        <span class="collapsible-icon">{{ isSectionExpanded('transform') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.transform' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('transform')) {
                        <div class="collapsible-content">
                          <p class="tip-text">{{ 'imageEditor.dragHandles' | transloco }}</p>
                          <div class="control-group with-input">
                            <label>{{ 'imageEditor.scaleH' | transloco }}</label>
                            <input type="range" min="10" max="300" [(ngModel)]="imageScaleX" (input)="onScaleXChange()" class="slider">
                            <input type="number" [(ngModel)]="imageScaleX" (change)="onScaleXChange()" class="number-input">
                          </div>
                          <div class="control-group with-input">
                            <label>{{ 'imageEditor.scaleV' | transloco }}</label>
                            <input type="range" min="10" max="300" [(ngModel)]="imageScaleY" (input)="onScaleYChange()" class="slider">
                            <input type="number" [(ngModel)]="imageScaleY" (change)="onScaleYChange()" class="number-input">
                          </div>
                          <div class="control-group with-input">
                            <label>{{ 'imageEditor.rotation' | transloco }}</label>
                            <input type="range" min="-180" max="180" [(ngModel)]="rotation" (input)="applyRotation()" class="slider">
                            <input type="number" [(ngModel)]="rotation" (change)="applyRotation()" class="number-input">
                          </div>
                          <div class="control-group with-input">
                            <label>{{ 'imageEditor.positionX' | transloco }}</label>
                            <input type="range" min="0" max="100" [(ngModel)]="imagePositionX" (input)="onPositionXChange()" class="slider">
                            <input type="number" [(ngModel)]="imagePositionX" (change)="onPositionXChange()" class="number-input">
                          </div>
                          <div class="control-group with-input">
                            <label>{{ 'imageEditor.positionY' | transloco }}</label>
                            <input type="range" min="0" max="100" [(ngModel)]="imagePositionY" (input)="onPositionYChange()" class="slider">
                            <input type="number" [(ngModel)]="imagePositionY" (change)="onPositionYChange()" class="number-input">
                          </div>
                          <div class="guides-row">
                            <span>{{ 'imageEditor.guides' | transloco }}</span>
                            <label class="checkbox-inline"><input type="checkbox" [(ngModel)]="showGuideGrid" (change)="onGuideChange()"> {{ 'imageEditor.grid' | transloco }}</label>
                            <label class="checkbox-inline"><input type="checkbox" [(ngModel)]="showCenterLines" (change)="onGuideChange()"> {{ 'imageEditor.center' | transloco }}</label>
                            <label class="checkbox-inline"><input type="checkbox" [(ngModel)]="showRuleOfThirds" (change)="onGuideChange()"> {{ 'imageEditor.thirds' | transloco }}</label>
                          </div>
                        </div>
                      }
                    </div>
                    <!-- ===== Aspect Ratio Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('aspectRatio')">
                        <span class="collapsible-icon">{{ isSectionExpanded('aspectRatio') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.aspectRatio' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('aspectRatio')) {
                        <div class="collapsible-content">
                          <p class="tip-text">{{ 'imageEditor.applyStandardRatios' | transloco }}</p>
                          <!-- Orientation Toggle -->
                          <div class="orientation-toggle">
                            <button class="orientation-btn" [class.active]="aspectOrientation === 'landscape'" (click)="setAspectOrientation('landscape')">
                              ↔ {{ 'imageEditor.landscape' | transloco }}
                            </button>
                            <button class="orientation-btn" [class.active]="aspectOrientation === 'portrait'" (click)="setAspectOrientation('portrait')">
                              ↕ {{ 'imageEditor.portrait' | transloco }}
                            </button>
                          </div>
                          <!-- Ratio Preset Buttons -->
                          <div class="ratio-presets">
                            @for (ratio of aspectRatios; track ratio) {
                              <button
                                class="ratio-btn"
                                [class.active]="selectedAspectRatio === ratio.value"
                                (click)="applyAspectRatioPreset(ratio.value)">
                                {{ ratio.label.split(' ')[0] }}
                              </button>
                            }
                          </div>
                          <button class="btn-secondary btn-small" (click)="resetAspectRatio()" style="margin-top: 8px; width: 100%;">
                            {{ 'imageEditor.resetToOriginal' | transloco }}
                          </button>
                        </div>
                      }
                    </div>
                    <!-- ===== Basic Adjustments Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('basic')">
                        <span class="collapsible-icon">{{ isSectionExpanded('basic') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.basicAdjustments' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('basic')) {
                        <div class="collapsible-content">
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.brightness' | transloco }}</span><span class="control-value">{{ enhancements.brightness }}%</span></label>
                            <input type="range" min="0" max="200" [(ngModel)]="enhancements.brightness" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.contrast' | transloco }}</span><span class="control-value">{{ enhancements.contrast }}%</span></label>
                            <input type="range" min="0" max="200" [(ngModel)]="enhancements.contrast" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.saturation' | transloco }}</span><span class="control-value">{{ enhancements.saturation }}%</span></label>
                            <input type="range" min="0" max="200" [(ngModel)]="enhancements.saturation" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.blur' | transloco }}</span><span class="control-value">{{ enhancements.blur }}px</span></label>
                            <input type="range" min="0" max="20" [(ngModel)]="enhancements.blur" (input)="onEnhancementChange()" class="slider">
                          </div>
                        </div>
                      }
                    </div>
                    <!-- ===== Color Correction Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('color')">
                        <span class="collapsible-icon">{{ isSectionExpanded('color') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.colorCorrection' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('color')) {
                        <div class="collapsible-content">
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.temperature' | transloco }}</span><span class="control-value">{{ enhancements.temperature }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.temperature" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.tint' | transloco }}</span><span class="control-value">{{ enhancements.tint }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.tint" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.vibrance' | transloco }}</span><span class="control-value">{{ enhancements.vibrance }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.vibrance" (input)="onEnhancementChange()" class="slider">
                          </div>
                        </div>
                      }
                    </div>
                    <!-- ===== Levels Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('levels')">
                        <span class="collapsible-icon">{{ isSectionExpanded('levels') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.levels' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('levels')) {
                        <div class="collapsible-content">
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.highlights' | transloco }}</span><span class="control-value">{{ enhancements.highlights }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.highlights" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.midtones' | transloco }}</span><span class="control-value">{{ enhancements.midtones }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.midtones" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.shadows' | transloco }}</span><span class="control-value">{{ enhancements.shadows }}</span></label>
                            <input type="range" min="-100" max="100" [(ngModel)]="enhancements.shadows" (input)="onEnhancementChange()" class="slider">
                          </div>
                        </div>
                      }
                    </div>
                    <!-- ===== Effects Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('effects')">
                        <span class="collapsible-icon">{{ isSectionExpanded('effects') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.effects' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('effects')) {
                        <div class="collapsible-content">
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.grayscale' | transloco }}</span><span class="control-value">{{ enhancements.grayscale }}%</span></label>
                            <input type="range" min="0" max="100" [(ngModel)]="enhancements.grayscale" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.sepia' | transloco }}</span><span class="control-value">{{ enhancements.sepia }}%</span></label>
                            <input type="range" min="0" max="100" [(ngModel)]="enhancements.sepia" (input)="onEnhancementChange()" class="slider">
                          </div>
                          <div class="control-group">
                            <label><span class="control-label">{{ 'imageEditor.sharpen' | transloco }}</span><span class="control-value">{{ enhancements.sharpen }}</span></label>
                            <input type="range" min="0" max="100" [(ngModel)]="enhancements.sharpen" (input)="onEnhancementChange()" class="slider">
                          </div>
                        </div>
                      }
                    </div>
                    <button class="btn btn-secondary btn-full-width" (click)="resetEnhancements()" style="margin-top: 12px;">
                      {{ 'imageEditor.resetAllEnhancements' | transloco }}
                    </button>
                  </div>
                </div>
              }
              <!-- ==================== BACKGROUND ACCORDION ==================== -->
              @if (activeAccordion === 'background') {
                <div class="accordion-panel">
                  <div class="accordion-header">
                    <span>{{ 'imageEditor.backgroundSettings' | transloco }}</span>
                  </div>
                  <div class="accordion-body">
                    <!-- Background Removal Status -->
                    @if (backgroundRemoved) {
                      <div class="status-badge success" style="margin-bottom: 12px;">
                        ✓ {{ 'imageEditor.backgroundRemoved' | transloco }}
                      </div>
                    }
                    @if (backgroundRemoved) {
                      <button class="btn btn-secondary btn-full-width" (click)="restoreOriginalBackground()" style="margin-bottom: 16px;">
                        {{ 'imageEditor.restoreOriginalBackground' | transloco }}
                      </button>
                    }
                    <!-- ===== Presets Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('presets')">
                        <span class="collapsible-icon">{{ isSectionExpanded('presets') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.presetsType' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('presets')) {
                        <div class="collapsible-content">
                          <div class="preset-buttons">
                            <button class="preset-btn" (click)="applyQuickPreset('white')">{{ 'imageEditor.white' | transloco }}</button>
                            <button class="preset-btn" (click)="applyQuickPreset('gray')">{{ 'imageEditor.gray' | transloco }}</button>
                            <button class="preset-btn gradient" (click)="applyQuickPreset('gradient')">{{ 'imageEditor.gradient' | transloco }}</button>
                            <button class="preset-btn warm" (click)="applyQuickPreset('warm')">{{ 'imageEditor.warm' | transloco }}</button>
                            <button class="preset-btn dark" (click)="applyQuickPreset('dramatic')">{{ 'imageEditor.dark' | transloco }}</button>
                          </div>
                          <div class="control-group">
                            <label>{{ 'imageEditor.backgroundType' | transloco }}</label>
                            <select [(ngModel)]="backgroundType" (change)="onBackgroundTypeChange()" class="form-select">
                              <option value="solid">{{ 'imageEditor.solidColor' | transloco }}</option>
                              <option value="gradient">{{ 'imageEditor.gradient' | transloco }}</option>
                              <option value="image">{{ 'imageEditor.imagePixabay' | transloco }}</option>
                            </select>
                          </div>
                        </div>
                      }
                    </div>
                    <!-- ===== Color Settings Section ===== -->
                    @if (backgroundType === 'solid' || backgroundType === 'gradient') {
                      <div class="collapsible-section">
                        <div class="collapsible-header" (click)="toggleSection('bg-color')">
                          <span class="collapsible-icon">{{ isSectionExpanded('bg-color') ? '−' : '+' }}</span>
                          <span class="collapsible-title">{{ backgroundType === 'solid' ? 'Solid Color' : 'Gradient' }}</span>
                        </div>
                        @if (isSectionExpanded('bg-color')) {
                          <div class="collapsible-content">
                            <!-- Solid Color Picker -->
                            @if (backgroundType === 'solid') {
                              <div class="control-group">
                                <label>{{ 'imageEditor.backgroundColor' | transloco }}</label>
                                <input type="color" [(ngModel)]="backgroundColor" (change)="applyEnhancementsWithBackground()" class="color-picker">
                              </div>
                            }
                            <!-- Gradient Controls -->
                            @if (backgroundType === 'gradient') {
                              <div class="gradient-controls">
                                <div class="control-group">
                                  <label>{{ 'imageEditor.color1' | transloco }}</label>
                                  <input type="color" [(ngModel)]="gradientColor1" (change)="applyEnhancementsWithBackground()" class="color-picker">
                                </div>
                                <div class="control-group">
                                  <label>{{ 'imageEditor.color2' | transloco }}</label>
                                  <input type="color" [(ngModel)]="gradientColor2" (change)="applyEnhancementsWithBackground()" class="color-picker">
                                </div>
                                <div class="control-group">
                                  <label><span class="control-label">{{ 'imageEditor.angle' | transloco }}</span><span class="control-value">{{ gradientAngle }}°</span></label>
                                  <input type="range" min="0" max="360" [(ngModel)]="gradientAngle" (input)="applyEnhancementsWithBackground()" class="slider">
                                </div>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                    <!-- ===== Image Library Section ===== -->
                    <div class="collapsible-section">
                      <div class="collapsible-header" (click)="toggleSection('bg-library')">
                        <span class="collapsible-icon">{{ isSectionExpanded('bg-library') ? '−' : '+' }}</span>
                        <span class="collapsible-title">{{ 'imageEditor.imageLibrary' | transloco }}</span>
                      </div>
                      @if (isSectionExpanded('bg-library')) {
                        <div class="collapsible-content">
                          <!-- Pixabay Browser -->
                          <button class="btn btn-pixabay btn-full-width" (click)="openPixabayBrowser()">
                            🖼️ {{ 'imageEditor.browsePixabayImages' | transloco }}
                            <span class="btn-subtitle">{{ 'imageEditor.freeHighQualityImages' | transloco }}</span>
                          </button>
                          <!-- Pixabay Panel - Now opens in separate modal -->
                          @if (false) {
                            <div class="pixabay-panel" style="display: none;">
                              <div class="pixabay-categories">
                                @for (cat of pixabayService.categories; track cat) {
                                  <button
                                    class="category-btn"
                                    [class.active]="pixabaySelectedCategory === cat.name"
                                    (click)="searchPixabayByCategory(cat.name)">
                                    {{ cat.name }}
                                  </button>
                                }
                              </div>
                              <div class="pixabay-search">
                                <input type="text" [(ngModel)]="pixabaySearchQuery" [placeholder]="'imageEditor.searchImages' | transloco" (keyup.enter)="searchPixabay()">
                                <button class="search-btn" (click)="searchPixabay()">🔍</button>
                              </div>
                              @if (pixabayLoading) {
                                <div class="pixabay-loading">
                                  <div class="spinner"></div>
                                  <span>{{ 'imageEditor.loadingImages' | transloco }}</span>
                                </div>
                              }
                              @if (pixabaySearchResults.length > 0) {
                                <div class="pixabay-grid">
                                  @for (image of pixabaySearchResults; track image) {
                                    <div class="pixabay-thumbnail" (click)="selectPixabayBackground(image)">
                                      <img [src]="image.previewURL" [alt]="image.tags">
                                    </div>
                                  }
                                </div>
                              }
                              @if (pixabayHasMore && !pixabayLoading) {
                                <div class="pixabay-load-more">
                                  <button class="btn btn-secondary btn-sm" (click)="loadMorePixabay()">
                                    {{ 'imageEditor.loadMoreImages' | transloco }}
                                  </button>
                                  <span class="pixabay-count">{{ 'imageEditor.showingOf' | transloco: { shown: pixabaySearchResults.length, total: pixabayTotalHits } }}</span>
                                </div>
                              }
                              @if (pixabayLoading && pixabaySearchResults.length > 0) {
                                <div class="pixabay-loading-more">
                                  <div class="spinner-small"></div>
                                  <span>{{ 'imageEditor.loadingMore' | transloco }}</span>
                                </div>
                              }
                              @if (!pixabayLoading && pixabaySearchResults.length === 0 && pixabaySelectedCategory) {
                                <div class="pixabay-no-results">
                                  <p>{{ 'imageEditor.noImagesFound' | transloco }}</p>
                                </div>
                              }
                              <div class="pixabay-attribution">
                                {{ 'imageEditor.imagesBy' | transloco }} <a href="https://pixabay.com" target="_blank">Pixabay</a>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                    <!-- ===== AI Background Generation Section ===== -->
                    @if (backgroundRemoved) {
                      <div>
                        <button class="btn btn-ai btn-full-width" (click)="showBriaModal = true">
                          🤖 {{ 'imageEditor.aiBackgroundPremium' | transloco }}
                          @if (briaTrialStatus.isTrialActive) {
                            <span class="trial-badge">
                              {{ 'imageEditor.freeCount' | transloco: { remaining: briaTrialStatus.remaining, limit: briaTrialStatus.limit } }}
                            </span>
                          }
                          <span class="btn-subtitle">{{ 'imageEditor.poweredByBria' | transloco }}</span>
                        </button>
                      </div>
                    }
                    <!-- ===== Background Image Adjustments (when image selected) ===== -->
                    @if (backgroundType === 'image' && backgroundImage) {
                      <div>
                        <!-- Position & Size Section -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('bg-position')">
                            <span class="collapsible-icon">{{ isSectionExpanded('bg-position') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.positionSize' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('bg-position')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.positionX' | transloco }}</span><span class="control-value">{{ backgroundImagePositionX }}%</span></label>
                                <input type="range" min="0" max="100" [(ngModel)]="backgroundImagePositionX" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.positionY' | transloco }}</span><span class="control-value">{{ backgroundImagePositionY }}%</span></label>
                                <input type="range" min="0" max="100" [(ngModel)]="backgroundImagePositionY" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.scale' | transloco }}</span><span class="control-value">{{ backgroundImageScale }}%</span></label>
                                <input type="range" min="10" max="300" [(ngModel)]="backgroundImageScale" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                            </div>
                          }
                        </div>
                        <!-- Enhancements Section -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('bg-enhance')">
                            <span class="collapsible-icon">{{ isSectionExpanded('bg-enhance') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.enhancements' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('bg-enhance')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.brightness' | transloco }}</span><span class="control-value">{{ backgroundImageBrightness }}%</span></label>
                                <input type="range" min="0" max="200" [(ngModel)]="backgroundImageBrightness" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.contrast' | transloco }}</span><span class="control-value">{{ backgroundImageContrast }}%</span></label>
                                <input type="range" min="0" max="200" [(ngModel)]="backgroundImageContrast" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.saturation' | transloco }}</span><span class="control-value">{{ backgroundImageSaturation }}%</span></label>
                                <input type="range" min="0" max="200" [(ngModel)]="backgroundImageSaturation" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.temperature' | transloco }}</span><span class="control-value">{{ backgroundImageTemperature > 0 ? '+' : '' }}{{ backgroundImageTemperature }}</span></label>
                                <input type="range" min="-100" max="100" [(ngModel)]="backgroundImageTemperature" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.tint' | transloco }}</span><span class="control-value">{{ backgroundImageTint > 0 ? '+' : '' }}{{ backgroundImageTint }}</span></label>
                                <input type="range" min="-100" max="100" [(ngModel)]="backgroundImageTint" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                            </div>
                          }
                        </div>
                        <!-- Levels Section -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('bg-levels')">
                            <span class="collapsible-icon">{{ isSectionExpanded('bg-levels') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.levels' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('bg-levels')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.highlights' | transloco }}</span><span class="control-value">{{ backgroundImageHighlights }}</span></label>
                                <input type="range" min="-100" max="100" [(ngModel)]="backgroundImageHighlights" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.midtones' | transloco }}</span><span class="control-value">{{ backgroundImageMidtones }}</span></label>
                                <input type="range" min="-100" max="100" [(ngModel)]="backgroundImageMidtones" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.shadows' | transloco }}</span><span class="control-value">{{ backgroundImageShadows }}</span></label>
                                <input type="range" min="-100" max="100" [(ngModel)]="backgroundImageShadows" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                            </div>
                          }
                        </div>
                        <!-- Effects Section -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('bg-effects')">
                            <span class="collapsible-icon">{{ isSectionExpanded('bg-effects') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.effects' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('bg-effects')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.opacity' | transloco }}</span><span class="control-value">{{ backgroundImageOpacity }}%</span></label>
                                <input type="range" min="0" max="100" [(ngModel)]="backgroundImageOpacity" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.blur' | transloco }}</span><span class="control-value">{{ backgroundImageBlur }}px</span></label>
                                <input type="range" min="0" max="20" [(ngModel)]="backgroundImageBlur" (input)="onBackgroundImageChange()" class="slider">
                              </div>
                              <button class="btn btn-secondary btn-full-width" (click)="clearBackgroundImage()" style="margin-top: 12px;">
                                {{ 'imageEditor.removeBackgroundImage' | transloco }}
                              </button>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
              <!-- ==================== SHADOW ACCORDION ==================== -->
              @if (activeAccordion === 'shadow') {
                <div class="accordion-panel">
                  <div class="accordion-header">
                    <span>{{ 'imageEditor.shadowSettings' | transloco }}</span>
                  </div>
                  <div class="accordion-body">
                    <label class="checkbox-label" style="margin-bottom: 12px;">
                      <input type="checkbox" [(ngModel)]="shadowLayerEnabled" (change)="onShadowToggle()">
                      <span>{{ 'imageEditor.enableShadow' | transloco }}</span>
                    </label>
                    @if (shadowLayerEnabled) {
                      <div>
                        <!-- ===== Setup Section ===== -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('shadow-setup')">
                            <span class="collapsible-icon">{{ isSectionExpanded('shadow-setup') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.presetShape' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('shadow-setup')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label>{{ 'imageEditor.shadowPreset' | transloco }}</label>
                                <select [(ngModel)]="shadowLayerPreset" (change)="loadShadowPreset(shadowLayerPreset)" class="form-select">
                                  @for (preset of shadowPresets; track preset) {
                                    <option [value]="preset.id">{{ preset.name }}</option>
                                  }
                                </select>
                              </div>
                              <div class="control-group">
                                <label>{{ 'imageEditor.shadowShape' | transloco }}</label>
                                <select [(ngModel)]="shadowLayerShape" (change)="onShadowShapeChange()" class="form-select">
                                  <option value="silhouette">{{ 'imageEditor.silhouetteFollows' | transloco }}</option>
                                  <option value="ellipse">{{ 'imageEditor.ellipse' | transloco }}</option>
                                  <option value="rectangle">{{ 'imageEditor.rectangle' | transloco }}</option>
                                </select>
                              </div>
                            </div>
                          }
                        </div>
                        <!-- ===== Position & Size Section ===== -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('shadow-position')">
                            <span class="collapsible-icon">{{ isSectionExpanded('shadow-position') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.positionSize' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('shadow-position')) {
                            <div class="collapsible-content">
                              <div class="control-group with-input">
                                <label>{{ 'imageEditor.offsetX' | transloco }}</label>
                                <input type="range" min="-800" max="800" [(ngModel)]="shadowLayerOffsetX" (input)="onShadowChange()" class="slider">
                                <input type="number" [(ngModel)]="shadowLayerOffsetX" (change)="onShadowChange()" class="number-input" style="width: 60px;">
                              </div>
                              <div class="control-group with-input">
                                <label>{{ 'imageEditor.offsetY' | transloco }}</label>
                                <input type="range" min="-800" max="800" [(ngModel)]="shadowLayerOffsetY" (input)="onShadowChange()" class="slider">
                                <input type="number" [(ngModel)]="shadowLayerOffsetY" (change)="onShadowChange()" class="number-input" style="width: 60px;">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.scale' | transloco }}</span><span class="control-value">{{ shadowLayerScale }}%</span></label>
                                <input type="range" min="10" max="200" [(ngModel)]="shadowLayerScale" (input)="onShadowChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.rotation' | transloco }}</span><span class="control-value">{{ shadowLayerRotation }}°</span></label>
                                <input type="range" min="-180" max="180" [(ngModel)]="shadowLayerRotation" (input)="onShadowChange()" class="slider">
                              </div>
                              <p class="tip-text" style="margin-top: 8px;">{{ 'imageEditor.dragShadowTip' | transloco }}</p>
                            </div>
                          }
                        </div>
                        <!-- ===== Appearance Section ===== -->
                        <div class="collapsible-section">
                          <div class="collapsible-header" (click)="toggleSection('shadow-appearance')">
                            <span class="collapsible-icon">{{ isSectionExpanded('shadow-appearance') ? '−' : '+' }}</span>
                            <span class="collapsible-title">{{ 'imageEditor.appearance' | transloco }}</span>
                          </div>
                          @if (isSectionExpanded('shadow-appearance')) {
                            <div class="collapsible-content">
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.blur' | transloco }}</span><span class="control-value">{{ shadowLayerBlur }}px</span></label>
                                <input type="range" min="0" max="50" [(ngModel)]="shadowLayerBlur" (input)="onShadowChange()" class="slider">
                              </div>
                              <div class="control-group">
                                <label><span class="control-label">{{ 'imageEditor.opacity' | transloco }}</span><span class="control-value">{{ shadowLayerOpacity }}%</span></label>
                                <input type="range" min="0" max="100" [(ngModel)]="shadowLayerOpacity" (input)="onShadowChange()" class="slider">
                              </div>
                            </div>
                          }
                        </div>
                        <button class="btn btn-secondary btn-full-width" (click)="resetShadowLayer()" style="margin-top: 12px;">
                          {{ 'imageEditor.resetShadow' | transloco }}
                        </button>
                      </div>
                    }
                    @if (!shadowLayerEnabled) {
                      <p class="tip-text" style="margin-top: 8px;">{{ 'imageEditor.enableShadowToShow' | transloco }}</p>
                    }
                  </div>
                </div>
              }
              <!-- ==================== EXPORT ACCORDION ==================== -->
              @if (activeAccordion === 'export') {
                <div class="accordion-panel">
                  <div class="accordion-header">
                    <span>{{ 'imageEditor.exportOptions' | transloco }}</span>
                  </div>
                  <div class="accordion-body">
                    <div class="section-title">{{ 'imageEditor.downloadFinalImage' | transloco }}</div>
                    <p class="tip-text">{{ 'imageEditor.exportComposedPng' | transloco }}</p>
                    <button class="btn btn-success btn-full-width" (click)="downloadImage()">
                      {{ 'imageEditor.downloadImage' | transloco }}
                    </button>
                    <div class="accordion-divider"></div>
                    <div class="section-title">{{ 'imageEditor.projectFile' | transloco }}</div>
                    <p class="tip-text">{{ 'imageEditor.saveSettingsToReopen' | transloco }}</p>
                    <button class="btn btn-primary btn-full-width" (click)="saveProject()">
                      {{ 'imageEditor.saveProject' | transloco }}
                    </button>
                    <button class="btn btn-secondary btn-full-width" (click)="triggerProjectFileInput()" style="margin-top: 8px;">
                      {{ 'imageEditor.openProject' | transloco }}
                    </button>
                  </div>
                </div>
              }
              <!-- ==================== DEFAULT: Welcome Panel ==================== -->
              @if (!activeAccordion && !perspectiveCorrectionMode) {
                <div class="accordion-panel welcome-panel">
                  <div class="welcome-content">
                    <h3>{{ 'imageEditor.imageEditor' | transloco }}</h3>
                    <p>{{ 'imageEditor.selectActionToStart' | transloco }}</p>
                    <ul class="welcome-actions">
                      <li class="welcome-action" (click)="triggerFileInput()">
                        <span class="welcome-icon">+</span>
                        <span class="welcome-label">{{ 'imageEditor.image' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.loadNewImage' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="toggleAccordion('enhance')">
                        <span class="welcome-icon">🎨</span>
                        <span class="welcome-label">{{ 'imageEditor.enhance' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.adjustTransformColor' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="removeBackground()" [class.disabled]="isProcessing || backgroundRemoved">
                        <span class="welcome-icon">✂️</span>
                        <span class="welcome-label">{{ 'imageEditor.removeBg' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.aiBackgroundRemoval' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="toggleAccordion('background')">
                        <span class="welcome-icon">🖼️</span>
                        <span class="welcome-label">{{ 'imageEditor.background' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.setColorOrImage' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="toggleAccordion('shadow')">
                        <span class="welcome-icon">◐</span>
                        <span class="welcome-label">{{ 'imageEditor.shadow' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.addShadowBeneath' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="startPerspectiveCorrection()">
                        <span class="welcome-icon">📐</span>
                        <span class="welcome-label">{{ 'imageEditor.perspective' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.fixPerspectiveDistortion' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="togglePerspectiveWarpMode()">
                        <span class="welcome-icon">🔶</span>
                        <span class="welcome-label">{{ 'imageEditor.warp' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.distortImageCorners' | transloco }}</span>
                      </li>
                      <li class="welcome-action" (click)="toggleAccordion('export')">
                        <span class="welcome-icon">💾</span>
                        <span class="welcome-label">{{ 'imageEditor.export' | transloco }}</span>
                        <span class="welcome-desc">{{ 'imageEditor.downloadOrSaveProject' | transloco }}</span>
                      </li>
                    </ul>
                    <p class="tip-text">{{ 'imageEditor.orClickLayer' | transloco }}</p>
                  </div>
                </div>
              }
              <!-- ==================== PERSPECTIVE CORRECTION MODE ==================== -->
              @if (perspectiveCorrectionMode) {
                <div class="accordion-panel">
                  <div class="accordion-header">
                    <span>{{ 'imageEditor.perspectiveCorrection' | transloco }}</span>
                  </div>
                  <div class="accordion-body">
                    <div class="info-box">
                      {{ 'imageEditor.clickFourCorners' | transloco }}
                    </div>
                    <div class="corner-status" style="margin: 12px 0; text-align: center;">
                      {{ 'imageEditor.cornersSelected' | transloco: { count: corners.length } }}
                    </div>
                    <button class="btn btn-warning btn-full-width" (click)="resetCorners()" style="margin-top: 8px;">
                      {{ 'imageEditor.resetCorners' | transloco }}
                    </button>
                    <button class="btn btn-success btn-full-width" (click)="applyPerspectiveCorrection()" [disabled]="corners.length !== 4" style="margin-top: 8px;">
                      {{ 'imageEditor.applyCorrection' | transloco }}
                    </button>
                    <button class="btn btn-link btn-full-width" (click)="cancelPerspectiveCorrection()" style="margin-top: 8px;">
                      {{ 'imageEditor.cancel' | transloco }}
                    </button>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Pixabay Image Browser Modal -->
@if (showPixabayModal) {
  <div class="image-browser-modal" (click)="showPixabayModal = false">
    <div class="modal-content-large" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ 'imageEditor.browsePixabayImages' | transloco }}</h3>
        <button class="close-btn" (click)="showPixabayModal = false">&times;</button>
      </div>
      <div class="modal-body">
        <div class="pixabay-categories">
          @for (cat of pixabayService.categories; track cat) {
            <button
              class="category-btn"
              [class.active]="pixabaySelectedCategory === cat.name"
              (click)="searchPixabayByCategory(cat.name)">
              {{ cat.name }}
            </button>
          }
        </div>
        <div class="pixabay-search">
          <input type="text" [(ngModel)]="pixabaySearchQuery" [placeholder]="'imageEditor.searchImages' | transloco" (keyup.enter)="searchPixabay()">
          <button class="search-btn" (click)="searchPixabay()">🔍</button>
        </div>
        @if (pixabayLoading) {
          <div class="pixabay-loading">
            <div class="spinner"></div>
            <span>{{ 'imageEditor.loadingImages' | transloco }}</span>
          </div>
        }
        @if (pixabaySearchResults.length > 0) {
          <div class="pixabay-grid">
            @for (image of pixabaySearchResults; track image) {
              <div class="pixabay-thumbnail" (click)="selectPixabayBackground(image)">
                <img [src]="image.previewURL" [alt]="image.tags">
              </div>
            }
          </div>
        }
        @if (pixabayHasMore && !pixabayLoading) {
          <div class="pixabay-load-more">
            <button class="btn btn-secondary btn-sm" (click)="loadMorePixabay()">
              {{ 'imageEditor.loadMoreImages' | transloco }}
            </button>
            <span class="pixabay-count">{{ 'imageEditor.showingOf' | transloco: { shown: pixabaySearchResults.length, total: pixabayTotalHits } }}</span>
          </div>
        }
        @if (pixabayLoading && pixabaySearchResults.length > 0) {
          <div class="pixabay-loading-more">
            <div class="spinner-small"></div>
            <span>{{ 'imageEditor.loadingMore' | transloco }}</span>
          </div>
        }
        @if (!pixabayLoading && pixabaySearchResults.length === 0 && pixabaySelectedCategory) {
          <div class="pixabay-no-results">
            <p>{{ 'imageEditor.noImagesFound' | transloco }}</p>
          </div>
        }
        <div class="pixabay-attribution">
          {{ 'imageEditor.imagesBy' | transloco }} <a href="https://pixabay.com" target="_blank">Pixabay</a>
        </div>
      </div>
    </div>
  </div>
}

<!-- Bria AI Background Generator Modal -->
@if (showBriaModal) {
  <div class="image-browser-modal" (click)="showBriaModal = false">
    <div class="modal-content-large" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ 'imageEditor.aiBackgroundGeneratorPremium' | transloco }}</h3>
        @if (briaTrialStatus.isTrialActive) {
          <span class="trial-badge">
            {{ 'imageEditor.freeCount' | transloco: { remaining: briaTrialStatus.remaining, limit: briaTrialStatus.limit } }}
          </span>
        }
        <button class="close-btn" (click)="showBriaModal = false">&times;</button>
      </div>
      <div class="modal-body">
        <!-- Trial Limit Reached Warning -->
        @if (!briaTrialStatus.isTrialActive) {
          <div class="ai-trial-warning">
            <span class="warning-icon">!</span>
            <span>{{ 'imageEditor.freeTrialLimitReached' | transloco }}</span>
          </div>
        }
        @if (briaTrialStatus.isTrialActive) {
          <div>
            <p class="tip-text">{{ 'imageEditor.generateAiBackgrounds' | transloco }}</p>
            <!-- Scene Description Presets -->
            <div class="ai-preset-buttons">
              @for (preset of briaScenePresets; track preset) {
                <button
                  class="ai-preset-btn"
                  [class.active]="briaScenePrompt === preset.prompt"
                  (click)="selectBriaPreset(preset)">
                  {{ preset.name }}
                </button>
              }
            </div>
            <!-- Custom Prompt Input -->
            <div class="control-group">
              <label>{{ 'imageEditor.sceneDescription' | transloco }}</label>
              <textarea
                [(ngModel)]="briaScenePrompt"
                [placeholder]="'imageEditor.describeBackgroundScene' | transloco"
                rows="3"
                class="scene-prompt-input"
              [disabled]="briaGenerating"></textarea>
            </div>
            <!-- Result Preview (shown above generate button) -->
            @if (briaResultPreview && !briaGenerating) {
              <div class="ai-result-preview">
                <p class="tip-text">{{ 'imageEditor.previewClickApply' | transloco }}</p>
                <img [src]="briaResultPreview" [alt]="'imageEditor.generatedBackgroundPreview' | transloco">
                <div class="ai-result-actions">
                  <button class="btn btn-secondary btn-sm" (click)="discardAiResult()">{{ 'imageEditor.discard' | transloco }}</button>
                  <button class="btn btn-success btn-sm" (click)="applyAiResult()">{{ 'imageEditor.applyBackground' | transloco }}</button>
                </div>
              </div>
            }
            <!-- Generate Button -->
            <button
              class="btn btn-ai-generate btn-full-width"
              (click)="generateAiBackground()"
              [disabled]="briaGenerating || !briaScenePrompt.trim()">
              @if (!briaGenerating) {
                <span>{{ 'imageEditor.generateAiBackground' | transloco }}</span>
              }
              @if (briaGenerating) {
                <span>
                  <span class="spinner-inline"></span>
                  {{ 'imageEditor.generating' | transloco }}
                </span>
              }
            </button>
            <!-- Error Message -->
            @if (briaError) {
              <div class="ai-error-message">
                {{ briaError }}
              </div>
            }
            <p class="tip-text" style="margin-top: 12px;">
              {{ 'imageEditor.poweredBy' | transloco }} <a href="https://bria.ai" target="_blank">Bria.ai</a>
            </p>
          </div>
        }
      </div>
    </div>
  </div>
}

<!-- Hidden file inputs -->
<input type="file" #fileInput (change)="onFileSelected($event)" accept="image/*" style="display: none;">
<input type="file" #projectFileInput (change)="onProjectFileSelected($event)" accept=".artflow,.json" style="display: none;">
